/**
 * Tentaläge (slice D, P2-1; persona report §4, rulings 5 and 9). Pure: the caller passes the
 * course's exam dates (`examOn` and `retakesOn` in `content/courses/*.json`, never asked of the
 * student) and the local day key (`dayKey(now, "Europe/Stockholm")`).
 *
 * - The next exam is the first exam date on or after `today`: the ordinary exam, then each
 *   omtenta in turn. After the last one there is none.
 * - Tentaläge is on by itself from `EXAM_MODE_WINDOW_DAYS` days before that exam through the exam
 *   day, or whenever the student answered "Jag repeterar inför tentan" in Kom igång.
 * - A course without `examOn` has no window: only the "repeterar" answer turns Tentaläge on, and
 *   it then shows no countdown.
 *
 * Tentaläge never opens a lecture: release windows (slice B, P1-12) still decide what a student
 * may draw. It only turns the "Bara det vi gått igenom" default off (ruling 5) and puts the exam
 * card first on Home.
 */
import { daysBetween } from "./time.js";
import type { CoursePosition } from "./userPrefs.js";

/** Tentaläge starts this many days before the exam (persona report §4). */
export const EXAM_MODE_WINDOW_DAYS = 14;

export interface CourseExamDates {
  /** The ordinary exam, `YYYY-MM-DD`. */
  examOn?: string;
  /** The omtentor, `YYYY-MM-DD`, in any order. */
  retakesOn?: readonly string[];
}

export type ExamDateKind = "exam" | "retake";

export interface UpcomingExam {
  date: string;
  kind: ExamDateKind;
  /** Whole days from `today` to the exam; 0 on the exam day. */
  daysLeft: number;
}

export type ExamModeReason = "window" | "reviewing";

export interface ExamModeState {
  active: boolean;
  /** Why it is on; null when it is off. The window wins when both hold. */
  reason: ExamModeReason | null;
  /** The next exam, if the course has one on or after `today`. */
  exam: UpcomingExam | null;
}

/** Every exam date of the course in date order, each with its kind. */
export function examDates(dates: CourseExamDates): { date: string; kind: ExamDateKind }[] {
  const all: { date: string; kind: ExamDateKind }[] = [];
  if (dates.examOn) all.push({ date: dates.examOn, kind: "exam" });
  for (const date of dates.retakesOn ?? []) all.push({ date, kind: "retake" });
  return all.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
}

/** The first exam on or after `today`, or null. */
export function upcomingExam(dates: CourseExamDates, today: string): UpcomingExam | null {
  const next = examDates(dates).find((exam) => exam.date >= today);
  return next ? { ...next, daysLeft: daysBetween(today, next.date) } : null;
}

export function examModeState(
  dates: CourseExamDates | null | undefined,
  today: string,
  position?: CoursePosition | null,
): ExamModeState {
  const exam = dates ? upcomingExam(dates, today) : null;
  if (exam && exam.daysLeft <= EXAM_MODE_WINDOW_DAYS) {
    return { active: true, reason: "window", exam };
  }
  if (position === "reviewing") return { active: true, reason: "reviewing", exam };
  return { active: false, reason: null, exam };
}
